/**
 * CrossSalesDashboard - Dashboard Ventes Croisées
 *
 * Sections:
 * 1. Vue globale - KPIs des paires de produits
 * 2. Top Paires - Graphique des meilleures paires
 * 3. Table détaillée - Liste des paires avec métriques
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  ShoppingCart,
  Package,
  TrendingUp,
  Loader2,
  ChevronDown,
  ChevronUp,
  AlertCircle,
  X,
  RefreshCw,
  Zap,
  Layers,
  ArrowRight,
  HelpCircle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import './CrossSalesDashboard.css';
import config from '../config';
import googleAdsLogo from '../logos/google-ads.png';

const API_URL = config.API_URL;

// Couleurs pour le graphique
const CHART_COLORS = ['#6366f1', '#8b5cf6', '#a855f7', '#c084fc', '#d8b4fe', '#e9d5ff'];

const CrossSalesDashboard = ({ token, globalConnectors, selectedPeriod = 'LAST_30_DAYS', selectedClientId, selectedAccount: parentSelectedAccount }) => {
  // Get sourceId from parent selected account
  const sourceId = parentSelectedAccount?.sourceId || parentSelectedAccount?.id;
  const accountName = parentSelectedAccount?.name || 'Compte';

  // State - Data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [pairs, setPairs] = useState([]);
  const [triggers, setTriggers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    chart: true,
    triggers: true,
    categories: true,
    table: false,
  });

  // State - Tri des catégories
  const [categorySortConfig, setCategorySortConfig] = useState({ key: 'revenue', direction: 'desc' });

  // State - Tri des triggers
  const [triggersSortConfig, setTriggersSortConfig] = useState({ key: 'totalCrossSellOccurrences', direction: 'desc' });

  // State - Tri des paires
  const [pairsSortConfig, setPairsSortConfig] = useState({ key: 'coOccurrences', direction: 'desc' });

  // State - Filtre par campagne
  const [selectedCampaignFilter, setSelectedCampaignFilter] = useState(null);
  const [availableCampaigns, setAvailableCampaigns] = useState([]);
  const [campaignSearchQuery, setCampaignSearchQuery] = useState('');
  const [showCampaignAutocomplete, setShowCampaignAutocomplete] = useState(false);

  // State - Paramètres
  const [minCoOccurrences, setMinCoOccurrences] = useState(2);

  // Toggle section
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Tri des triggers
  const handleTriggersSort = (key) => {
    let direction = 'desc';
    if (triggersSortConfig.key === key && triggersSortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setTriggersSortConfig({ key, direction });
  };

  // Triggers triés
  const sortedTriggers = [...triggers].sort((a, b) => {
    const { key, direction } = triggersSortConfig;
    let aVal = a[key];
    let bVal = b[key];

    if (aVal == null) aVal = 0;
    if (bVal == null) bVal = 0;

    if (direction === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  // Tri des paires
  const handlePairsSort = (key) => {
    let direction = 'desc';
    if (pairsSortConfig.key === key && pairsSortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setPairsSortConfig({ key, direction });
  };

  // Paires triées
  const sortedPairs = [...pairs].sort((a, b) => {
    const { key, direction } = pairsSortConfig;
    let aVal = a[key];
    let bVal = b[key];

    if (aVal == null) aVal = 0;
    if (bVal == null) bVal = 0;

    if (direction === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  // Tri des catégories
  const handleCategorySort = (key) => {
    let direction = 'desc';
    if (categorySortConfig.key === key && categorySortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setCategorySortConfig({ key, direction });
  };

  // Catégories triées
  const sortedCategories = [...categories].sort((a, b) => {
    const { key, direction } = categorySortConfig;
    let aVal = a[key];
    let bVal = b[key];

    // Gestion des valeurs null/undefined
    if (aVal == null) aVal = 0;
    if (bVal == null) bVal = 0;

    if (direction === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  // Fetch data
  const fetchData = useCallback(async () => {
    if (!sourceId || !token) return;

    setLoading(true);
    setError(null);

    const headers = { Authorization: `Bearer ${token}` };
    const campaignIdParam = selectedCampaignFilter?.id || selectedCampaignFilter?.campaignId;

    try {
      // Fetch campaigns for filter
      const campaignsRes = await axios.get(
        `${API_URL}/api/dashboard/cross-sales/campaigns`,
        {
          headers,
          params: { sourceId, dateRange: selectedPeriod },
        }
      ).catch(() => ({ data: { data: { campaigns: [] } } }));

      if (campaignsRes.data?.data?.campaigns) {
        setAvailableCampaigns(campaignsRes.data.data.campaigns);
      }

      // Fetch all data in parallel
      const [overviewRes, pairsRes, triggersRes, categoriesRes] = await Promise.all([
        axios.get(
          `${API_URL}/api/dashboard/cross-sales/overview`,
          {
            headers,
            params: {
              sourceId,
              dateRange: selectedPeriod,
              ...(campaignIdParam && { campaignId: campaignIdParam }),
              minCoOccurrences,
            },
          }
        ),
        axios.get(
          `${API_URL}/api/dashboard/cross-sales/pairs`,
          {
            headers,
            params: {
              sourceId,
              dateRange: selectedPeriod,
              ...(campaignIdParam && { campaignId: campaignIdParam }),
              minCoOccurrences,
              limit: 50,
            },
          }
        ),
        axios.get(
          `${API_URL}/api/dashboard/cross-sales/triggers`,
          {
            headers,
            params: {
              sourceId,
              dateRange: selectedPeriod,
              ...(campaignIdParam && { campaignId: campaignIdParam }),
              minCoOccurrences,
              limit: 15,
            },
          }
        ).catch(() => ({ data: { data: { triggers: [] } } })),
        axios.get(
          `${API_URL}/api/dashboard/cross-sales/categories`,
          {
            headers,
            params: {
              sourceId,
              dateRange: selectedPeriod,
              ...(campaignIdParam && { campaignId: campaignIdParam }),
              minCoOccurrences,
            },
          }
        ).catch(() => ({ data: { data: { categories: [] } } })),
      ]);

      if (overviewRes.data?.success) {
        setOverview(overviewRes.data.data);
      }

      if (pairsRes.data?.success) {
        setPairs(pairsRes.data.data.pairs || []);
      }

      if (triggersRes.data?.success) {
        setTriggers(triggersRes.data.data.triggers || []);
      }

      if (categoriesRes.data?.success) {
        setCategories(categoriesRes.data.data.categories || []);
      }

    } catch (err) {
      console.error('Erreur chargement cross-sales:', err);
      setError(err.response?.data?.error || err.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [sourceId, token, selectedPeriod, selectedCampaignFilter, minCoOccurrences]);

  // Effect: fetch on mount and when deps change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Click outside to close autocomplete
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showCampaignAutocomplete && !e.target.closest('.campaign-filter-container')) {
        setShowCampaignAutocomplete(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showCampaignAutocomplete]);

  // Format number
  const formatNumber = (num, decimals = 0) => {
    if (num === null || num === undefined) return '-';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(num);
  };

  // Truncate product name helper
  const truncateName = (name, maxLen = 30) => {
    if (!name) return '';
    return name.length > maxLen ? name.slice(0, maxLen) + '...' : name;
  };

  // Prepare chart data (top 10 pairs)
  const chartData = pairs.slice(0, 10).map((pair) => ({
    name: `${truncateName(pair.product1Title || pair.product1Id, 25)} + ${truncateName(pair.product2Title || pair.product2Id, 25)}`,
    displayName: `${truncateName(pair.product1Title || pair.product1Id, 20)}`,
    coOccurrences: pair.coOccurrences,
    lift: pair.lift,
    product1: pair.product1Title || pair.product1Id,
    product2: pair.product2Title || pair.product2Id,
  }));

  return (
    <div className="cross-sales-dashboard">
      {/* Sticky Campaign Filter Banner - Style identique au dashboard global */}
      {availableCampaigns.length > 0 && (
        <div style={{
          background: '#f9fafb',
          padding: '16px 20px',
          borderRadius: '8px',
          marginTop: '-5px',
          marginBottom: '10px',
          border: '1px solid #e5e7eb',
          position: 'sticky',
          top: '0',
          zIndex: '1000',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
            <span style={{
              fontWeight: '500',
              color: '#374151',
              fontSize: '0.95em',
              display: 'flex',
              alignItems: 'center'
            }}>
              <img src={googleAdsLogo} alt="Google Ads" style={{width: '24px', height: '24px', marginRight: '8px'}} /> Filtrer:
            </span>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <input
                type="text"
                placeholder="Rechercher une campagne..."
                value={selectedCampaignFilter ? (selectedCampaignFilter.name || selectedCampaignFilter.campaignName) : campaignSearchQuery}
                onChange={(e) => {
                  if (selectedCampaignFilter) {
                    setSelectedCampaignFilter(null);
                  }
                  setCampaignSearchQuery(e.target.value);
                  setShowCampaignAutocomplete(true);
                }}
                onFocus={() => setShowCampaignAutocomplete(true)}
                onBlur={() => {
                  setTimeout(() => setShowCampaignAutocomplete(false), 200);
                }}
                style={{
                  padding: '4px 12px',
                  paddingRight: (campaignSearchQuery || selectedCampaignFilter) ? '28px' : '12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  backgroundColor: selectedCampaignFilter ? '#dbeafe' : 'white',
                  color: '#374151',
                  fontSize: '0.9em',
                  width: '400px',
                  height: '35px',
                  boxSizing: 'border-box',
                  outline: 'none'
                }}
              />
              {(campaignSearchQuery || selectedCampaignFilter) && (
                <button
                  onClick={() => {
                    setCampaignSearchQuery('');
                    setSelectedCampaignFilter(null);
                    setShowCampaignAutocomplete(false);
                  }}
                  style={{
                    position: 'absolute',
                    right: '6px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: selectedCampaignFilter ? '#1a1a1a' : '#9ca3af',
                    padding: '2px',
                    fontSize: '14px',
                    lineHeight: 1,
                    zIndex: 1
                  }}
                  title={selectedCampaignFilter ? "Effacer le filtre campagne" : "Effacer la recherche"}
                >
                  ✕
                </button>
              )}
              {/* Autocomplete dropdown */}
              {showCampaignAutocomplete && (() => {
                const searchLower = campaignSearchQuery.toLowerCase().trim();
                const filteredCampaigns = searchLower
                  ? [...availableCampaigns]
                      .filter(c => (c.name || c.campaignName || '').toLowerCase().includes(searchLower))
                      .sort((a, b) => (a.name || a.campaignName || '').localeCompare(b.name || b.campaignName || ''))
                  : [...availableCampaigns].sort((a, b) => (a.name || a.campaignName || '').localeCompare(b.name || b.campaignName || ''));

                if (filteredCampaigns.length === 0) {
                  return (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      background: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      maxHeight: '300px',
                      overflowY: 'auto',
                      zIndex: 1001,
                      padding: '12px',
                      color: '#6b7280',
                      fontSize: '0.9em'
                    }}>
                      Aucune campagne trouvée
                    </div>
                  );
                }

                return (
                  <div style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: 'white',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    zIndex: 1001
                  }}>
                    {filteredCampaigns.map(campaign => (
                      <div
                        key={campaign.id || campaign.campaignId}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setSelectedCampaignFilter(campaign);
                          setCampaignSearchQuery('');
                          setShowCampaignAutocomplete(false);
                        }}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          borderBottom: '1px solid #f3f4f6',
                          backgroundColor: selectedCampaignFilter?.id === campaign.id ? '#FAF9F5' : 'white',
                          transition: 'background-color 0.15s'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                        onMouseLeave={(e) => e.currentTarget.style.backgroundColor = selectedCampaignFilter?.id === campaign.id ? '#FAF9F5' : 'white'}
                      >
                        <div style={{ fontWeight: '500', color: '#374151' }}>{campaign.name || campaign.campaignName}</div>
                        <div style={{ fontSize: '0.85em', color: '#6b7280' }}>{campaign.channelType || 'Campagne'}</div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="cs-header">
        <div className="cs-header-title">
          <ShoppingCart size={24} />
          <h2>Ventes Croisées</h2>
        </div>
      </div>

      {/* Loading state */}
      {loading && (
        <div className="cs-loading">
          <Loader2 size={32} className="spinning" />
          <span>Chargement des données...</span>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="cs-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Content */}
      {!loading && !error && (
        <>
          {/* Analyse textuelle */}
          {(categories.length > 0 || triggers.length > 0) && (
            <div className="cs-analysis-section">
              <div className="cs-analysis-content">
                {(() => {
                  // Calculs pour l'analyse
                  const topCategory = categories.length > 0 ? categories.reduce((a, b) => a.revenue > b.revenue ? a : b) : null;
                  const topTrigger = triggers.length > 0 ? triggers[0] : null;
                  const avgCrossSellRate = categories.length > 0
                    ? Math.round(categories.reduce((sum, cat) => sum + cat.crossSellRate, 0) / categories.length)
                    : 0;
                  const highCrossSellCategories = categories.filter(cat => cat.crossSellRate >= 50);
                  const topInterCategory = categories.length > 0
                    ? categories.reduce((a, b) => a.crossCategoryCrossSell > b.crossCategoryCrossSell ? a : b)
                    : null;

                  return (
                    <>
                      <h3>Synthèse Cross-Sell</h3>
                      <div className="cs-analysis-grid">
                        {topCategory && (
                          <div className="cs-analysis-card">
                            <div className="cs-analysis-icon" style={{ background: '#f97316' }}>€</div>
                            <div className="cs-analysis-text">
                              <strong>{topCategory.category}</strong> est la catégorie avec le plus de revenus (<strong>{formatNumber(topCategory.revenue, 0)} €</strong>) et un taux de cross-sell de <strong>{topCategory.crossSellRate}%</strong>.
                            </div>
                          </div>
                        )}

                        {topTrigger && (
                          <div className="cs-analysis-card">
                            <div className="cs-analysis-icon" style={{ background: '#6366f1' }}><Zap size={16} /></div>
                            <div className="cs-analysis-text">
                              <strong>{topTrigger.productTitle}</strong> est le meilleur produit déclencheur avec <strong>{topTrigger.totalCrossSellOccurrences}</strong> cross-sells générés.
                            </div>
                          </div>
                        )}

                        {highCrossSellCategories.length > 0 && (
                          <div className="cs-analysis-card">
                            <div className="cs-analysis-icon" style={{ background: '#10b981' }}><TrendingUp size={16} /></div>
                            <div className="cs-analysis-text">
                              <strong>{highCrossSellCategories.length}</strong> catégorie{highCrossSellCategories.length > 1 ? 's' : ''} avec un taux de cross-sell supérieur à 50% : {highCrossSellCategories.slice(0, 3).map(c => c.category).join(', ')}.
                            </div>
                          </div>
                        )}

                        {topInterCategory && topInterCategory.crossCategoryCrossSell > 0 && (
                          <div className="cs-analysis-card">
                            <div className="cs-analysis-icon" style={{ background: '#8b5cf6' }}><ArrowRight size={16} /></div>
                            <div className="cs-analysis-text">
                              <strong>{topInterCategory.category}</strong> génère le plus de cross-sell inter-catégories ({topInterCategory.crossCategoryCrossSell} occurrences)
                              {topInterCategory.topPartners.length > 0 && <>, notamment avec <strong>{topInterCategory.topPartners[0].category}</strong></>}.
                            </div>
                          </div>
                        )}
                      </div>

                      {avgCrossSellRate > 0 && (
                        <p className="cs-analysis-summary">
                          En moyenne, <strong>{avgCrossSellRate}%</strong> des conversions génèrent du cross-sell (achats multiples le même jour).
                        </p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Categories Section - Analyse par catégorie */}
          {categories.length > 0 && (
            <div className="cs-section">
              <div className="cs-section-header" onClick={() => toggleSection('categories')}>
                <h3>
                  <Layers size={18} />
                  Analyse par Catégorie ({categories.length})
                </h3>
                {expandedSections.categories ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
              {expandedSections.categories && (
                <div className="cs-categories-content">
                  {/* Graphiques visuels */}
                  <div className="cs-categories-charts">
                    {/* Revenus par catégorie avec barres */}
                    <div className="cs-category-revenue-chart">
                      <h4>Taux de cross-sell par catégorie</h4>
                      <div className="cs-category-bars">
                        {/* En-têtes */}
                        <div className="cs-category-bar-row cs-category-bar-header">
                          <div className="cs-category-bar-label">Catégorie</div>
                          <div className="cs-category-bar-container">
                            <span className="cs-category-rate" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              Taux
                              <span className="cs-tooltip-container">
                                <HelpCircle size={12} className="cs-tooltip-icon" />
                                <span className="cs-tooltip-text">Pourcentage de conversions ayant généré du cross-sell</span>
                              </span>
                            </span>
                          </div>
                          <div className="cs-category-bar-values">
                            <span className="cs-category-revenue">Revenu</span>
                          </div>
                        </div>
                        {sortedCategories.map((cat, index) => {
                          const barWidth = cat.crossSellRate; // Le taux est déjà en pourcentage (0-100)
                          const colors = ['#6366f1', '#8b5cf6', '#a855f7', '#06b6d4', '#14b8a6', '#10b981', '#f97316', '#ef4444', '#eab308', '#ec4899'];
                          return (
                            <div key={index} className="cs-category-bar-row">
                              <div className="cs-category-bar-label">{cat.category}</div>
                              <div className="cs-category-bar-container">
                                <div
                                  className="cs-category-bar-fill"
                                  style={{ width: `${barWidth}%`, backgroundColor: colors[index % colors.length] }}
                                />
                                <span className="cs-bar-percentage">{cat.crossSellRate}%</span>
                              </div>
                              <div className="cs-category-bar-values">
                                <span className="cs-category-revenue">{formatNumber(cat.revenue, 0)} €</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                  </div>

                  {/* Tableau détaillé en dessous */}
                  <div className="campaigns-table-wrapper cs-categories-table">
                    <table className="campaigns-table">
                      <thead>
                        <tr>
                          <th onClick={() => handleCategorySort('category')} className="sortable">
                            Catégorie {categorySortConfig.key === 'category' && (categorySortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleCategorySort('productCount')} className="sortable numeric">
                            Produits {categorySortConfig.key === 'productCount' && (categorySortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleCategorySort('conversions')} className="sortable numeric">
                            Conv. {categorySortConfig.key === 'conversions' && (categorySortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleCategorySort('revenue')} className="sortable numeric">
                            Revenu {categorySortConfig.key === 'revenue' && (categorySortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          <th onClick={() => handleCategorySort('sameCategoryCrossSell')} className="sortable numeric">
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              Intra-cat.
                              <span className="cs-tooltip-container" onClick={(e) => e.stopPropagation()}>
                                <HelpCircle size={14} className="cs-tooltip-icon" />
                                <span className="cs-tooltip-text">Cross-sell au sein de la catégorie : plusieurs produits de cette même catégorie achetés ensemble</span>
                              </span>
                              {categorySortConfig.key === 'sameCategoryCrossSell' && (categorySortConfig.direction === 'asc' ? '▲' : '▼')}
                            </span>
                          </th>
                          <th onClick={() => handleCategorySort('crossCategoryCrossSell')} className="sortable numeric">
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              Inter-cat.
                              <span className="cs-tooltip-container" onClick={(e) => e.stopPropagation()}>
                                <HelpCircle size={14} className="cs-tooltip-icon" />
                                <span className="cs-tooltip-text">Cross-sell vers d'autres catégories : produits de cette catégorie achetés avec des produits d'autres catégories</span>
                              </span>
                              {categorySortConfig.key === 'crossCategoryCrossSell' && (categorySortConfig.direction === 'asc' ? '▲' : '▼')}
                            </span>
                          </th>
                          <th onClick={() => handleCategorySort('crossSellRate')} className="sortable numeric">
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              Taux
                              <span className="cs-tooltip-container" onClick={(e) => e.stopPropagation()}>
                                <HelpCircle size={14} className="cs-tooltip-icon" />
                                <span className="cs-tooltip-text">Pourcentage de conversions ayant généré du cross-sell (achats multiples)</span>
                              </span>
                              {categorySortConfig.key === 'crossSellRate' && (categorySortConfig.direction === 'asc' ? '▲' : '▼')}
                            </span>
                          </th>
                          <th>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                              Top partenaires
                              <span className="cs-tooltip-container">
                                <HelpCircle size={14} className="cs-tooltip-icon" />
                                <span className="cs-tooltip-text">Catégories les plus souvent achetées en même temps que les produits de cette catégorie</span>
                              </span>
                            </span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedCategories.map((cat, index) => (
                          <tr key={index}>
                            <td className="campaign-name">
                              {cat.category}
                            </td>
                            <td className="numeric">{cat.productCount}</td>
                            <td className="numeric">{formatNumber(cat.conversions, 1)}</td>
                            <td className="numeric">{formatNumber(cat.revenue, 2)} €</td>
                            <td className="numeric">
                              <span className="cs-badge-secondary">{cat.sameCategoryCrossSell}</span>
                            </td>
                            <td className="numeric">
                              <span className="cs-badge">{cat.crossCategoryCrossSell}</span>
                            </td>
                            <td className="numeric">
                              <span className={`cs-lift-value ${cat.crossSellRate > 30 ? 'positive' : ''}`}>
                                {cat.crossSellRate}%
                              </span>
                            </td>
                            <td>
                              <div className="cs-partners">
                                {cat.topPartners.slice(0, 2).map((partner, idx) => (
                                  <span key={idx} className="cs-partner-tag">
                                    {partner.category} ({partner.coOccurrences})
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Triggers Section - Produits déclencheurs */}
          {triggers.length > 0 && (
            <div className="cs-section">
              <div className="cs-section-header" onClick={() => toggleSection('triggers')}>
                <h3>
                  <Zap size={18} />
                  Produits Déclencheurs ({triggers.length})
                </h3>
                {expandedSections.triggers ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </div>
              {expandedSections.triggers && (
                <div className="campaigns-table-wrapper cs-triggers-table">
                  <table className="campaigns-table">
                    <thead>
                      <tr>
                        <th onClick={() => handleTriggersSort('productTitle')} className="sortable">
                          Produit {triggersSortConfig.key === 'productTitle' && (triggersSortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => handleTriggersSort('totalCrossSellOccurrences')} className="sortable numeric">
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            Cross-sells
                            <span className="cs-tooltip-container" onClick={(e) => e.stopPropagation()}>
                              <HelpCircle size={14} className="cs-tooltip-icon" />
                              <span className="cs-tooltip-text">Nombre total de produits achetés en même temps que ce produit</span>
                            </span>
                            {triggersSortConfig.key === 'totalCrossSellOccurrences' && (triggersSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </span>
                        </th>
                        <th onClick={() => handleTriggersSort('uniquePartners')} className="sortable numeric">
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            Partenaires
                            <span className="cs-tooltip-container" onClick={(e) => e.stopPropagation()}>
                              <HelpCircle size={14} className="cs-tooltip-icon" />
                              <span className="cs-tooltip-text">Nombre de produits différents achetés au moins une fois avec ce produit</span>
                            </span>
                            {triggersSortConfig.key === 'uniquePartners' && (triggersSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </span>
                        </th>
                        <th onClick={() => handleTriggersSort('ownConversions')} className="sortable numeric">
                          Conv. {triggersSortConfig.key === 'ownConversions' && (triggersSortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => handleTriggersSort('ownRevenue')} className="sortable numeric">
                          Revenu {triggersSortConfig.key === 'ownRevenue' && (triggersSortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => handleTriggersSort('partnerRevenue')} className="sortable numeric">
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            Rev. partenaires
                            <span className="cs-tooltip-container" onClick={(e) => e.stopPropagation()}>
                              <HelpCircle size={14} className="cs-tooltip-icon" />
                              <span className="cs-tooltip-text">Revenu généré par les produits achetés en même temps que ce produit</span>
                            </span>
                            {triggersSortConfig.key === 'partnerRevenue' && (triggersSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </span>
                        </th>
                        <th onClick={() => handleTriggersSort('crossSellRate')} className="sortable numeric">
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            Taux
                            <span className="cs-tooltip-container" onClick={(e) => e.stopPropagation()}>
                              <HelpCircle size={14} className="cs-tooltip-icon" />
                              <span className="cs-tooltip-text">Pourcentage de conversions ayant généré du cross-sell (achats multiples)</span>
                            </span>
                            {triggersSortConfig.key === 'crossSellRate' && (triggersSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedTriggers.map((trigger, index) => (
                        <tr key={index}>
                          <td className="campaign-name">
                            <div className="cs-product-title">{trigger.productTitle}</div>
                            <div className="cs-product-category">{trigger.category}</div>
                          </td>
                          <td className="numeric">
                            <span className="cs-badge">{trigger.totalCrossSellOccurrences}</span>
                          </td>
                          <td className="numeric">{trigger.uniquePartners}</td>
                          <td className="numeric">{formatNumber(trigger.ownConversions, 1)}</td>
                          <td className="numeric">{formatNumber(trigger.ownRevenue, 2)} €</td>
                          <td className="numeric">{formatNumber(trigger.partnerRevenue, 2)} €</td>
                          <td className="numeric">
                            <span className={`cs-lift-value ${trigger.crossSellRate > 50 ? 'positive' : ''}`}>
                              {trigger.crossSellRate}%
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Table Section */}
          <div className="cs-section">
            <div className="cs-section-header" onClick={() => toggleSection('table')}>
              <h3>
                <Package size={18} />
                Détail des paires ({pairs.length})
              </h3>
              {expandedSections.table ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
            </div>
            {expandedSections.table && (
              <div className="campaigns-table-wrapper cs-pairs-table">
                {pairs.length === 0 ? (
                  <div className="cs-no-data">
                    <Package size={48} />
                    <p>Aucune paire de produits détectée avec les critères actuels.</p>
                    <p className="hint">Essayez de réduire le minimum de co-achats ou d'élargir la période.</p>
                  </div>
                ) : (
                  <table className="campaigns-table">
                    <thead>
                      <tr>
                        <th onClick={() => handlePairsSort('product1Title')} className="sortable">
                          Produit 1 {pairsSortConfig.key === 'product1Title' && (pairsSortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => handlePairsSort('product2Title')} className="sortable">
                          Produit 2 {pairsSortConfig.key === 'product2Title' && (pairsSortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => handlePairsSort('coOccurrences')} className="sortable numeric">
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            Co-achats
                            <span className="cs-tooltip-container" onClick={(e) => e.stopPropagation()}>
                              <HelpCircle size={14} className="cs-tooltip-icon" />
                              <span className="cs-tooltip-text">Nombre de fois où ces deux produits ont été achetés ensemble</span>
                            </span>
                            {pairsSortConfig.key === 'coOccurrences' && (pairsSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </span>
                        </th>
                        <th onClick={() => handlePairsSort('lift')} className="sortable numeric">
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            Lift
                            <span className="cs-tooltip-container" onClick={(e) => e.stopPropagation()}>
                              <HelpCircle size={14} className="cs-tooltip-icon" />
                              <span className="cs-tooltip-text">Mesure l'association entre les produits. Lift &gt; 1 = achetés ensemble plus souvent que par hasard</span>
                            </span>
                            {pairsSortConfig.key === 'lift' && (pairsSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </span>
                        </th>
                        <th onClick={() => handlePairsSort('confidence')} className="sortable numeric">
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            Confiance
                            <span className="cs-tooltip-container" onClick={(e) => e.stopPropagation()}>
                              <HelpCircle size={14} className="cs-tooltip-icon" />
                              <span className="cs-tooltip-text">Probabilité d'acheter le produit B quand on achète le produit A</span>
                            </span>
                            {pairsSortConfig.key === 'confidence' && (pairsSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </span>
                        </th>
                        <th onClick={() => handlePairsSort('combinedRevenue')} className="sortable numeric">
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            Revenu combiné
                            <span className="cs-tooltip-container" onClick={(e) => e.stopPropagation()}>
                              <HelpCircle size={14} className="cs-tooltip-icon" />
                              <span className="cs-tooltip-text">Somme des revenus des deux produits sur la période</span>
                            </span>
                            {pairsSortConfig.key === 'combinedRevenue' && (pairsSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedPairs.map((pair, index) => (
                        <tr key={index}>
                          <td className="campaign-name">
                            <div className="cs-product-title">{pair.product1Title || pair.product1Id}</div>
                            <div className="cs-product-category">{pair.product1Category}</div>
                          </td>
                          <td className="campaign-name">
                            <div className="cs-product-title">{pair.product2Title || pair.product2Id}</div>
                            <div className="cs-product-category">{pair.product2Category}</div>
                          </td>
                          <td className="numeric">
                            <span className="cs-badge">{pair.coOccurrences}</span>
                          </td>
                          <td className="numeric">
                            <span className={`cs-lift-value ${pair.lift > 1 ? 'positive' : pair.lift < 1 ? 'negative' : ''}`}>
                              {pair.lift?.toFixed(2) || '-'}
                            </span>
                          </td>
                          <td className="numeric">{Math.min(pair.confidence * 100, 100)?.toFixed(1)}%</td>
                          <td className="numeric">{formatNumber(pair.combinedRevenue, 2)} €</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* No data state */}
      {!loading && !error && !overview && pairs.length === 0 && (
        <div className="cs-no-data-full">
          <ShoppingCart size={64} />
          <h3>Aucune donnée de ventes croisées</h3>
          <p>Ce compte n'a pas de données Shopping suffisantes pour analyser les ventes croisées.</p>
        </div>
      )}
    </div>
  );
};

export default CrossSalesDashboard;
